import { useContext, useEffect, useMemo } from 'react';
import { stack as d3stack } from '@visx/vendor/d3-shape';
import { stackOffset, stackOrder } from '@visx/shape';
import { extent } from '@visx/vendor/d3-array';
import DataContext from "../context/DataContext.js";
import getBarStackRegistryData from "../utils/getBarStackRegistryData.js";
import combineBarStackData from "../utils/combineBarStackData.js";
import getChildrenAndGrandchildrenWithProps from "../utils/getChildrenAndGrandchildrenWithProps.js";
export default function useStackedData(_ref) {
  let {
    children,
    order,
    offset
  } = _ref;
  const {
    horizontal,
    registerData,
    unregisterData
  } = useContext(DataContext);

  // find series children
  // @TODO: memoization doesn't work well if at all for this
  const seriesChildren = useMemo(() => getChildrenAndGrandchildrenWithProps(children), [children]);

  // extract data keys from child series
  const dataKeys = useMemo(() => seriesChildren.filter(child => child.props.dataKey).map(child => child.props.dataKey), [seriesChildren]);

  // group all child data by stack value { [x | y]: { [dataKey]: value } }
  // this format is needed by d3Stack
  const combinedData = useMemo(() => combineBarStackData(seriesChildren, horizontal), [horizontal, seriesChildren]);

  // stack data
  const stackedData = useMemo(() => {
    // automatically set offset to diverging if it's undefined and negative values are present
    const hasSomeNegativeValues = offset ? null : combinedData.some(d => d.negativeSum < 0);
    const stack = d3stack();
    stack.keys(dataKeys);
    if (order) stack.order(stackOrder(order));
    if (offset || hasSomeNegativeValues) stack.offset(stackOffset(offset || 'diverging'));
    return stack(combinedData);
  }, [combinedData, dataKeys, order, offset]);

  // update the domain to account for the (directional) stacked value
  const comprehensiveDomain = useMemo(() => extent(stackedData.reduce((allDatum, stack) => {
    stack.forEach(_ref2 => {
      let [min, max] = _ref2;
      allDatum.push(min);
      allDatum.push(max);
    });
    return allDatum;
  }, [])), [stackedData]);

  // register all child data using the stack-transformed values
  useEffect(() => {
    const dataToRegister = getBarStackRegistryData(stackedData, comprehensiveDomain, horizontal);
    registerData(dataToRegister);

    // unregister data on unmount
    return () => unregisterData(dataKeys);
  }, [dataKeys, comprehensiveDomain, horizontal, stackedData, registerData, unregisterData, seriesChildren]);
  return {
    seriesChildren,
    dataKeys,
    stackedData
  };
}